/**
 * BeatsStrip — carousel of story beat cards on a circular arc.
 *
 * Cards are positioned purely via transforms from a single Animated scroll
 * offset. No ScrollView — uses PanResponder for drag, spring snap on release.
 *
 * Each card, as it moves away from the centered/highlighted position:
 *   - Scales down from 1.0 (center) to 0.3 (off-screen edge)
 *   - Rotates along a circular arc of radius ARC_RADIUS (bottom-center pivot),
 *     so it curves down and away — the space under the arc is left for the caption
 *   - Translates Y along that arc
 *   - Has a fixed-size outer container (sized to the focused card) so selection
 *     never shifts layout
 *
 * Beat text (the focused beat's caption) is rendered below the cards at a fixed
 * bottom-center position, shown only when scrolling has stopped.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import { View, Text, Pressable, Image, Platform, useWindowDimensions, Animated, PanResponder } from "react-native";
import { styles } from "./StoryView.styles";
import { useScale, design, colors } from "../../styles";
import type { NarrativeBeat } from "../../store/storyStore";

/**
 * Radius of the virtual circle arc. Smaller = more curved: each card step
 * subtends a larger angle, so cards rotate more visibly as they scroll and the
 * arc dips further below center — the space under the arc is where the beat
 * caption sits. Tuned with the smaller beat-card size so the curve reads well
 * within the ~30% band.
 */
const ARC_RADIUS = 1100;
/** Minimum mouse movement to distinguish drag from tap (web) */
const DRAG_THRESHOLD = 3;

const BeatCardInner = memo(function BeatCardInner({
  beat, index, onPress,
}: {
  beat: NarrativeBeat; index: number; onPress: (idx: number) => void;
}) {
  const handlePress = useCallback(() => onPress(index), [onPress, index]);
  return (
    <Pressable style={{ flex: 1 }} onPress={handlePress}>
      <View style={[styles.beatImageWrap, { borderRadius: 0 }]}>
        {beat.imageBase64 ? (
          <Image source={{ uri: `data:image/jpeg;base64,${beat.imageBase64}` }} style={styles.beatImage} resizeMode="cover" resizeMethod="resize" />
        ) : beat.isModerated ? (
          <Text style={styles.beatLoading}>🚫</Text>
        ) : beat.imageLoading ? (
          <Text style={styles.beatLoading}>&#9203;</Text>
        ) : (
          <Text style={[styles.beatLoading, { opacity: 0.3 }]}>#{index + 1}</Text>
        )}
      </View>
    </Pressable>
  );
});

export default function BeatsStrip({ beats, height }: { beats: NarrativeBeat[]; height: number }) {
  const [focusedIdx, setFocusedIdx] = useState(0);
  const [textIdx, setTextIdx] = useState(0);
  const focusedRef = useRef(0);
  const cardRefs = useRef<Record<number, View | null>>({});
  const visualFocusRef = useRef(0);

  const { width: screenWidth } = useWindowDimensions();
  const { s } = useScale();

  const focusBorderWidth = s(design.beatFocused.borderWidth);
  const normalBorderWidth = 2;
  const swapBorderNative = useCallback((oldIdx: number, newIdx: number) => {
    if (oldIdx === newIdx) return;
    const oldCard = cardRefs.current[oldIdx];
    const newCard = cardRefs.current[newIdx];
    if (oldCard) (oldCard as any).setNativeProps?.({ style: { borderWidth: normalBorderWidth, borderColor: colors.borderBeat } });
    if (newCard) (newCard as any).setNativeProps?.({ style: { borderWidth: focusBorderWidth, borderColor: colors.borderFocus } });
    visualFocusRef.current = newIdx;
  }, [focusBorderWidth]);
  const scrollAnim = useRef(new Animated.Value(0)).current;
  const scrollOffset = useRef(0);
  useEffect(() => {
    const id = scrollAnim.addListener(({ value }) => { scrollOffset.current = value; });
    return () => scrollAnim.removeListener(id);
  }, [scrollAnim]);
  const textOpacity = useRef(new Animated.Value(1)).current;
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(fadeTimerRef.current), []);

  // Fade text out/in when selected beat changes
  const prevFocusedIdx = useRef(focusedIdx);
  useEffect(() => {
    if (prevFocusedIdx.current === textIdx) return;
    prevFocusedIdx.current = textIdx;
    textOpacity.setValue(0);
    clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      Animated.timing(textOpacity, { toValue: 1, duration: 150, useNativeDriver: true }).start();
    }, 100);
  }, [textIdx, textOpacity]);

  // All containers use the focused (largest) size so layout never shifts
  const cardW = s(design.beatFocused.width);
  const cardH = s(design.beatFocused.height);
  const gap = s(design.beat.gap);
  const step = cardW + gap;

  // Scroll offset for card i to be centered
  const scrollForIdx = useCallback((idx: number) => idx * step, [step]);
  const idxFromScroll = useCallback(
    (x: number) => Math.max(0, Math.min(Math.round(x / step), beats.length - 1)),
    [step, beats.length],
  );

  // How far off-screen (in steps) before a card reaches minimum scale
  const edgeSteps = Math.ceil(screenWidth / step) + 1;
  const arcSamples = edgeSteps + 1;

  // Pre-compute arc interpolation arrays.
  // The scroll offset maps to arc length. For a card n steps from center,
  // arc length = n*step. The angle θ = arcLength/R.
  // Circle position: x = R·sin(θ), y = R·(1-cos(θ))
  // Since linear scroll places card at x = n*step, we need
  // xCorrection = R·sin(θ) - n*step to pull it onto the circle.
  const arcData = useMemo(() => {
    const offsets: number[] = [];
    const rotOutputs: string[] = [];
    const yOutputs: number[] = [];
    const xPosOutputs: number[] = [];
    const scaleOutputs: number[] = [];
    const opacityOutputs: number[] = [];

    // Compute scales first
    const scales: number[] = [];
    for (let n = -arcSamples; n <= arcSamples; n++) {
      const t = Math.min(Math.abs(n) / edgeSteps, 1);
      // Ease-out curve: rapid drop to 0.5 near center, gradual to 0.3 at edge
      const eased = Math.pow(t, 0.4);
      scales.push(Math.max(0.3, 1 - eased * 0.7));
    }

    // Cumulative angles adjusted for scale so smaller cards sit closer
    const baseAngle = step / ARC_RADIUS;
    const mid = arcSamples;
    const angles: number[] = new Array(scales.length).fill(0);
    for (let i = mid + 1; i < scales.length; i++) {
      angles[i] = angles[i - 1] + baseAngle * (scales[i] + scales[i - 1]) / 2;
    }
    for (let i = mid - 1; i >= 0; i--) {
      angles[i] = angles[i + 1] - baseAngle * (scales[i] + scales[i + 1]) / 2;
    }

    for (let n = -arcSamples; n <= arcSamples; n++) {
      const scrollDx = n * step;
      const idx = n + mid;
      const theta = angles[idx];
      const circleX = ARC_RADIUS * Math.sin(theta);
      const circleY = ARC_RADIUS * (1 - Math.cos(theta));
      const t = Math.min(Math.abs(n) / edgeSteps, 1);
      const opacity = Math.max(0.8, 1 - t * 0.2);
      offsets.push(scrollDx);
      rotOutputs.push(`${(-theta * 180) / Math.PI}deg`);
      yOutputs.push(circleY);
      xPosOutputs.push(-circleX);
      scaleOutputs.push(scales[idx]);
      opacityOutputs.push(opacity);
    }
    return { offsets, rotOutputs, yOutputs, xPosOutputs, scaleOutputs, opacityOutputs };
  }, [step, edgeSteps, arcSamples]);

  // --- Snap logic ---
  const snapTo = useCallback((idx: number, velocity = 0) => {
    const clamped = Math.max(0, Math.min(idx, beats.length - 1));
    focusedRef.current = clamped;
    setFocusedIdx(clamped);
    setTextIdx(clamped);
    const target = scrollForIdx(clamped);
    Animated.spring(scrollAnim, {
      toValue: target,
      velocity: -velocity,
      useNativeDriver: true,
      damping: 20,
      stiffness: 200,
    }).start();
  }, [beats.length, scrollForIdx, scrollAnim]);

  // Auto-scroll to new/updated/removed beats
  const prevBeatsRef = useRef<NarrativeBeat[]>(beats);
  useEffect(() => {
    const prev = prevBeatsRef.current;
    prevBeatsRef.current = beats;
    if (beats.length === 0) { focusedRef.current = 0; setFocusedIdx(0); setTextIdx(0); return; }
    if (beats.length > prev.length) { snapTo(beats.length - 1); return; }
    if (beats.length < prev.length) {
      const prevId = prev[focusedRef.current]?.beatId;
      const newIdx = beats.findIndex((b) => b.beatId === prevId);
      snapTo(newIdx >= 0 ? newIdx : Math.min(focusedRef.current, beats.length - 1));
      return;
    }
    for (let i = 0; i < beats.length; i++) {
      const b = beats[i], p = prev[i];
      if (b !== p && (b.action !== p.action || b.visualDescription !== p.visualDescription || b.beatId !== p.beatId)) {
        snapTo(i); return;
      }
    }
  }, [beats, snapTo]);

  // --- PanResponder for native drag ---
  const panResponder = useMemo(() => {
    let startOffset = 0;
    return PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: (_, g) => Math.abs(g.dx) > DRAG_THRESHOLD,
      onPanResponderGrant: () => {
        scrollAnim.stopAnimation((value) => {
          scrollOffset.current = value;
          startOffset = value;
        });
      },
      onPanResponderMove: (_, g) => {
        const newOffset = startOffset - g.dx;
        scrollOffset.current = newOffset;
        scrollAnim.setValue(newOffset);
        const idx = idxFromScroll(newOffset);
        if (idx !== focusedRef.current) {
          swapBorderNative(focusedRef.current, idx);
          focusedRef.current = idx;
          setTextIdx(idx);
        }
      },
      onPanResponderRelease: (_, g) => {
        snapTo(idxFromScroll(scrollOffset.current), g.vx);
      },
    });
  }, [scrollAnim, idxFromScroll, snapTo, swapBorderNative]);

  // --- Web mouse drag ---
  const containerRef = useRef<View>(null);
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const node = (containerRef.current as any);
    if (!node) return;
    // react-native-web: the underlying DOM node
    const el = node instanceof HTMLElement ? node : node._nativeTag ? document.getElementById(String(node._nativeTag)) : null;
    if (!el) return;

    let startX = 0, startOffset = 0, dragged = false;

    const onDown = (e: MouseEvent) => {
      startX = e.clientX;
      scrollAnim.stopAnimation((value) => {
        scrollOffset.current = value;
        startOffset = value;
      });
      dragged = false;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp, true);
    };
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) dragged = true;
      const newOffset = startOffset - dx;
      scrollOffset.current = newOffset;
      scrollAnim.setValue(newOffset);
      const idx = idxFromScroll(newOffset);
      if (idx !== focusedRef.current) {
        swapBorderNative(focusedRef.current, idx);
        focusedRef.current = idx;
        setTextIdx(idx);
      }
    };
    const onUp = () => {
      el.style.cursor = "grab";
      el.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp, true);
      snapTo(idxFromScroll(scrollOffset.current));
    };
    const onClick = (e: MouseEvent) => {
      if (dragged) { e.stopPropagation(); dragged = false; }
    };

    el.style.cursor = "grab";
    el.addEventListener("mousedown", onDown);
    el.addEventListener("click", onClick, true);
    return () => {
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("click", onClick, true);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp, true);
    };
  }, [beats.length, scrollAnim, idxFromScroll, snapTo, swapBorderNative]);

  // Half card height for bottom-center pivot compensation
  const halfH = cardH / 2;

  // Pre-compute per-card interpolations so they aren't recreated on every render
  const cardInterps = useMemo(() => beats.map((_, i) => {
    const center = i * step;
    const inputRange = arcData.offsets.map((off) => center + off);
    const rotation = scrollAnim.interpolate({ inputRange, outputRange: arcData.rotOutputs, extrapolate: "clamp" as const });
    const arcY = scrollAnim.interpolate({ inputRange, outputRange: arcData.yOutputs, extrapolate: "clamp" as const });
    const arcX = scrollAnim.interpolate({ inputRange, outputRange: arcData.xPosOutputs, extrapolate: "clamp" as const });
    const scale = scrollAnim.interpolate({ inputRange, outputRange: arcData.scaleOutputs, extrapolate: "clamp" as const });
    const cardOpacity = scrollAnim.interpolate({ inputRange, outputRange: arcData.opacityOutputs, extrapolate: "clamp" as const });
    const translateX = Animated.add((screenWidth - cardW) / 2, arcX);
    return { rotation, arcY, scale, cardOpacity, translateX };
  }), [beats.length, step, arcData, scrollAnim, screenWidth, cardW]);

  const focusedBeat = beats[textIdx];

  return (
    <View style={[styles.beatsSection, { height }]}>
      {/* Beat text below cards in z-order */}
      {focusedBeat?.action ? (
        <Animated.View style={{
          position: "absolute" as const,
          top: cardH + s(4),
          left: 0,
          right: 0,
          alignItems: "center",
          opacity: textOpacity,
        }}>
          <Text
            style={[styles.beatText, { fontSize: s(design.beatText.fontSize), lineHeight: s(design.beatText.lineHeight), textAlign: "center", maxWidth: cardW }]}
            numberOfLines={2}
          >
            {focusedBeat.action}
          </Text>
        </Animated.View>
      ) : null}

      <View
        ref={containerRef}
        style={{ flex: 1, overflow: "visible" as const }}
        {...panResponder.panHandlers}
      >
        {beats.map((b, i) => {
          const focused = i === focusedIdx;
          const { rotation, arcY, scale, cardOpacity, translateX } = cardInterps[i] || {};

          return (
            <Animated.View
              key={b.beatId}
              ref={(ref: any) => { cardRefs.current[i] = ref; }}
              style={[
                styles.beatCard,
                focused && styles.beatCardFocused,
                b.isModerated && styles.beatCardModerated,
                {
                  position: "absolute" as const,
                  width: cardW,
                  height: cardH,
                  left: 0,
                  top: 0,
                  borderRadius: s(design.beatFocused.borderRadius),
                  opacity: cardOpacity,
                  transform: [
                    { translateX },
                    { translateY: arcY },
                    { translateY: halfH },
                    { rotate: rotation },
                    { scale },
                    { translateY: -halfH },
                  ],
                },
                focused && { borderWidth: s(design.beatFocused.borderWidth) },
              ]}
            >
              <View style={{ flex: 1, borderRadius: s(design.beatFocused.borderRadius), overflow: "hidden" as const }}>
                <BeatCardInner
                  beat={b}
                  index={i}
                  onPress={snapTo}
                />
              </View>
            </Animated.View>
          );
        })}
      </View>
    </View>
  );
}
